import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import type { FreelancerLink } from "@/lib/db-types";
import { linkLabelFromUrl, normalizeLinkUrl } from "@/lib/freelancers";

interface Props {
  value: FreelancerLink[];
  onChange: (links: FreelancerLink[]) => void;
}

/** Showreel, portefølje og andre lenker. Ugyldige URL-er avvises med melding. */
export function FreelancerLinksEditor({ value, onChange }: Props) {
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const normalized = normalizeLinkUrl(url);
    if (!normalized) {
      setError("Ugyldig lenke. Skriv for eksempel vimeo.com/123456.");
      return;
    }
    onChange([...value, { label: label.trim() || linkLabelFromUrl(normalized), url: normalized }]);
    setLabel("");
    setUrl("");
    setError(null);
  };

  return (
    <div className="space-y-2">
      {value.map((link, i) => (
        <div key={`${link.url}-${i}`} className="flex items-center gap-2 text-sm">
          <SrIcon name="external-link" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="font-medium">{link.label}</span>
          <span className="min-w-0 flex-1 truncate text-muted-foreground">{link.url}</span>
          <Button type="button" variant="ghost" size="sm" aria-label={`Fjern ${link.label}`} onClick={() => onChange(value.filter((_, j) => j !== i))}>
            <SrIcon name="x" className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Etikett (valgfri)" className="w-36" aria-label="Lenkeetikett" />
        <Input
          value={url}
          onChange={(e) => { setUrl(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="https://…"
          className="min-w-[12rem] flex-1"
          aria-label="Lenke"
          aria-invalid={!!error}
        />
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={!url.trim()}>
          <SrIcon name="plus" className="h-3.5 w-3.5" />
          Lenke
        </Button>
      </div>
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
